import { Link2, Plus, Trash2 } from 'lucide-react';
import React, { useRef, useState } from 'react';

import type { ChecklistData } from '../../../../types/blog';

interface Props {
  data: ChecklistData;
  onChange: (data: ChecklistData) => void;
}

interface LinkPopoverProps { onInsert: (url: string, anchor: string) => void; onClose: () => void; }
const LinkPopover: React.FC<LinkPopoverProps> = ({ onInsert, onClose }) => {
  const [url, setUrl] = useState('https://');
  const [anchor, setAnchor] = useState('');
  return (
    <div className="ds-card-pop absolute right-0 top-full mt-1 z-30 w-64 flex flex-col gap-2">
      <p className="t-card-title">Insert link</p>
      <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="https://example.com"
        className="ds-input ds-input-sm" />
      <input value={anchor} onChange={(e) => setAnchor(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="Anchor text (optional)"
        className="ds-input ds-input-sm" />
      <div className="flex gap-2">
        <button type="button" onClick={() => { if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); }}
          disabled={!url.trim() || url === 'https://'}
          className="ds-btn ds-btn-primary ds-btn-sm flex-1 disabled:opacity-40">Insert</button>
        <button type="button" onClick={onClose}
          className="ds-btn ds-btn-secondary ds-btn-sm flex-1">Cancel</button>
      </div>
    </div>
  );
};

const LinkedInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({
  value, onChange, placeholder,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [showLink, setShowLink] = useState(false);

  const handleInsert = (url: string, anchor: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: ss = 0, selectionEnd: se = 0 } = el;
    const text = anchor || value.slice(ss ?? 0, se ?? 0) || 'link';
    const md = `[${text}](${url})`;
    onChange(value.slice(0, ss ?? 0) + md + value.slice(se ?? 0));
    setShowLink(false);
    requestAnimationFrame(() => { el.focus(); });
  };

  return (
    <div className="relative flex-1">
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ds-input ds-input-sm pr-8"
      />
      <button
        type="button"
        title="Insert link"
        onClick={() => setShowLink((v) => !v)}
        className="ds-btn ds-btn-ghost ds-btn-sm absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 p-0"
        style={showLink ? { color: 'var(--ds-brand)' } : undefined}
      >
        <Link2 className="w-3 h-3" />
      </button>
      {showLink && <LinkPopover onInsert={handleInsert} onClose={() => setShowLink(false)} />}
    </div>
  );
};

export const ChecklistSectionEditor: React.FC<Props> = ({ data, onChange }) => {
  const update = (idx: number, val: string) => {
    const items = data.items.map((it, i) => (i === idx ? val : it));
    onChange({ items });
  };

  const add = () => onChange({ items: [...data.items, ''] });

  const remove = (idx: number) =>
    onChange({ items: data.items.filter((_, i) => i !== idx) });

  return (
    <div className="flex flex-col gap-2">
      {data.items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="w-4 h-4 rounded border-2 shrink-0" style={{ borderColor: 'var(--ds-ok)' }} />
          <LinkedInput
            value={item}
            onChange={(v) => update(idx, v)}
            placeholder={`Checklist item ${idx + 1} — click 🔗 to add a link`}
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            className="ds-btn ds-btn-ghost ds-btn-sm p-0 w-7 hover:text-[var(--ds-danger)]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="ds-btn ds-btn-secondary ds-btn-sm w-fit"
      >
        <Plus className="w-4 h-4" /> Add item
      </button>
    </div>
  );
};
